import { Route } from '@angular/router';
import {
  CanActivateGuard,
  LoginComponent,
  LogoutComponent,
  SSOResolver,
} from '@seech/auth-ng';
import { LayoutComponent } from './components/layout/layout.component';
import { privilegedRoleGuard } from './guards/privileged-role.guard';
import { workspaceAccessGuard } from './guards/workspace-access.guard';
import {
  PublishableViewerRouteDataKeys,
  PublishableViewerRouteFamilies,
  PublishableViewerRouteParameters,
} from './modules/features/publishables/models/publishable-viewer.models';

export const authRoutes: Route[] = [
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'logout',
    component: LogoutComponent,
  },
];

export const appRoutes: Route[] = [
  ...authRoutes,
  {
    path: 'publishables',
    canActivate: [CanActivateGuard.canActivate],
    loadChildren: () =>
      import('./modules/features/publishables/publishables.module').then(
        (m) => m.PublishablesModule
      ),
  },
  {
    path: `s/:${PublishableViewerRouteParameters.Token}`,
    data: {
      [PublishableViewerRouteDataKeys.RouteFamily]: PublishableViewerRouteFamilies.UnlistedTokenViewer,
      [PublishableViewerRouteDataKeys.RouteParameterName]: PublishableViewerRouteParameters.Token,
    },
    loadChildren: () =>
      import('./modules/features/publishables/publishables.module').then(
        (m) => m.PublishablesModule
      ),
  },
  {
    path: `p/:${PublishableViewerRouteParameters.Slug}`,
    data: {
      [PublishableViewerRouteDataKeys.RouteFamily]: PublishableViewerRouteFamilies.PublicSlugViewer,
      [PublishableViewerRouteDataKeys.RouteParameterName]: PublishableViewerRouteParameters.Slug,
    },
    loadChildren: () =>
      import('./modules/features/publishables/publishables.module').then(
        (m) => m.PublishablesModule
      ),
  },
  {
    path: '',
    component: LayoutComponent,
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'home',
      },
      {
        path: 'home',
        resolve: { auth: SSOResolver.resolve },
        loadChildren: () =>
          import('./modules/features/home/home.module').then(
            (m) => m.HomeModule
          ),
      },
      {
        path: 'work-requests',
        canActivate: [workspaceAccessGuard],
        loadChildren: () =>
          import('./modules/features/work-requests/work-requests.module').then(
            (m) => m.WorkRequestsModule
          ),
      },
      {
        path: 'approvals',
        canActivate: [workspaceAccessGuard, privilegedRoleGuard],
        loadChildren: () =>
          import('./modules/features/approvals/approvals.module').then(
            (m) => m.ApprovalsModule
          ),
      },
      {
        path: 'operations',
        canActivate: [workspaceAccessGuard, privilegedRoleGuard],
        loadChildren: () =>
          import('./modules/features/operations/operations.module').then(
            (m) => m.OperationsModule
          ),
      },
      {
        path: 'workspaces',
        canActivate: [workspaceAccessGuard],
        loadChildren: () =>
          import('./modules/features/workspaces/workspaces.module').then(
            (m) => m.WorkspacesModule
          ),
      },
      {
        path: 'credentials',
        canActivate: [workspaceAccessGuard, privilegedRoleGuard],
        loadChildren: () =>
          import('./modules/features/credentials/credentials.module').then(
            (m) => m.CredentialsModule
          ),
      },
      {
        path: 'connectors',
        canActivate: [workspaceAccessGuard, privilegedRoleGuard],
        loadChildren: () =>
          import('./modules/features/connectors/connectors.module').then(
            (m) => m.ConnectorsModule
          ),
      },
      {
        path: 'channel',
        canActivate: [workspaceAccessGuard],
        loadChildren: () =>
          import('./modules/features/channel/channel.module').then(
            (m) => m.ChannelModule
          ),
      },
      {
        path: 'chat/:sessionId',
        canActivate: [workspaceAccessGuard],
        loadChildren: () =>
          import('./modules/features/conversation/conversation.module').then(
            (m) => m.ConversationModule
          ),
      },
      {
        path: 'skills',
        pathMatch: 'full',
        redirectTo: 'marketplace/skills',
      },
      {
        path: 'skills/:tab',
        redirectTo: 'marketplace/skills',
      },
      {
        path: 'marketplace',
        canActivate: [workspaceAccessGuard],
        loadChildren: () =>
          import('./modules/features/marketplace/marketplace.module').then(
            (m) => m.MarketplaceModule
          ),
      },
      {
        path: 'agents',
        canActivate: [workspaceAccessGuard],
        loadChildren: () =>
          import('./modules/features/agents/agents.module').then(
            (m) => m.AgentsModule
          ),
      },
      {
        path: 'account',
        canActivate: [workspaceAccessGuard],
        loadChildren: () =>
          import('./modules/features/account/account.module').then(
            (m) => m.AccountModule
          ),
      },
      {
        path: 'notifications',
        canActivate: [workspaceAccessGuard],
        loadChildren: () =>
          import('./modules/features/notifications/notifications.module').then(
            (m) => m.NotificationsModule
          ),
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'home',
  },
];
